/**
 * useFolderTree Hook
 * 
 * Fetches and manages the hierarchical folder tree for a case and category.
 * Provides the tree structure with real-time updates via React Query.
 * 
 * @example
 * const { tree, isLoading, error, refresh } = useFolderTree(caseId, 'nos_pieces');
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { folderHierarchyService } from "@/services/folderHierarchyService";

interface UseFolderTreeOptions {
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTime?: number;
}

export function useFolderTree(
  caseId: string,
  category: string,
  subcategory?: string,
  options: UseFolderTreeOptions = {}
) {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    // L'arborescence change rarement ; pas de refetch au focus par défaut (évite
    // le re-render/repli de l'arbre au retour de fenêtre, #238). Surchargeable.
    refetchOnWindowFocus = false,
    staleTime = 30_000 // 30 seconds
  } = options;

  const query = useQuery({
    queryKey: ['folder-tree', caseId, category, subcategory],
    queryFn: async () => {
      if (!caseId || !category) {
        return [];
      }
      return folderHierarchyService.getFolderTree(caseId, category, subcategory);
    },
    enabled: enabled && !!caseId && !!category,
    refetchOnWindowFocus,
    staleTime
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['folder-tree', caseId, category, subcategory] });
  };

  return {
    tree: query.data || [],
    isLoading: query.isLoading,
    error: query.error,
    refresh,
    isFetching: query.isFetching
  };
}

